import CloseIcon from '@/public/assets/icons/close.svg'
import { InputProps, Box, Flex, Input, Text } from '@chakra-ui/react'
import { useState, useEffect, useRef } from 'react'
import { ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { USDC_NEW_DENOM } from '@/constants/constants'

export function StyledInput<IFormInput extends FieldValues>({
  title,
  icon,
  placeholder,
  control,
  disabled,
  validate = () => true,
  maxValue,
  errors,
  clearable,
  infoText,
  inputs,
  denom,
}: {
  title: string
  placeholder: string
  validate?: (value: string) => boolean
  errors?: string
  control: ControllerRenderProps<IFormInput>
  icon: React.ReactElement
  maxValue?: number
  disabled?: boolean
  clearable?: boolean
  infoText?: string
  inputs?: InputProps
  denom?: string
}) {
  const [isValid, setValid] = useState(true)
  const [isMounted, setMounted] = useState(false)
  const { setValue, setFocus } = useFormContext()

  const fiatStore = useFiatCurrencyStore()
  const isFiatMode = !!denom && denom === USDC_NEW_DENOM && !fiatStore.isUsd()

  const [fiatDisplayValue, setFiatDisplayValue] = useState<string>('')
  const isTypingRef = useRef(false)

  // Sync fiat display when USDC value changes externally (e.g. preset buttons)
  useEffect(() => {
    if (!isFiatMode) return
    if (isTypingRef.current) {
      isTypingRef.current = false
      return
    }
    const usdc = parseFloat(control.value)
    setFiatDisplayValue(!isNaN(usdc) && usdc > 0 ? String(parseFloat(fiatStore.usdcToFiat(usdc).toFixed(6))) : '')
  }, [control.value, isFiatMode])

  useEffect(() => {
    isMounted && setValid(!errors)
    setMounted(true)
  }, [control.value, errors])

  useEffect(() => {
    if (!control.value) return
    if (maxValue && Number(control.value) > maxValue) control.onChange(maxValue)
  }, [control.value])

  const handleClear = () => {
    setValue((control as any).name, '', { shouldValidate: true })
    setFocus((control as any).name)
    if (isFiatMode) setFiatDisplayValue('')
  }

  const inputValue = maxValue && Number(control.value) > maxValue ? maxValue : control.value

  // Hint shown on the right: opposite of what's in the input
  const usdcHint = isFiatMode && control.value ? `≈ ${parseFloat(Number(control.value).toFixed(4))} USDC` : null

  return (
    <Box
      px={3}
      py={2}
      bg={'ninja.500'}
      opacity={disabled ? 0.6 : 1}
      border={!isValid ? '1px solid red' : '1px solid transparent'}
      rounded={'lg'}
      flexBasis={'full'}
    >
      <Flex justifyContent={'space-between'} color={'ninja.200'} fontWeight={600} fontSize={'xs'}>
        <Text>{title}</Text>
        {infoText ? <Text>{infoText}</Text> : null}
      </Flex>
      <Flex alignItems={'center'} gap={1.5}>
        {!isFiatMode && (
          <Box flexShrink={0} h={'18px'} w={'18px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            {icon}
          </Box>
        )}
        <Input
          ref={control.ref}
          disabled={disabled}
          onBlur={control.onBlur}
          onChange={(event) => {
            if (isFiatMode) {
              const raw = event.target.value.replace(',', '.')
              if (raw && !/^[\d]*\.?[\d]*$/.test(raw)) return
              setFiatDisplayValue(raw)
              isTypingRef.current = true
              const fiatNum = parseFloat(raw)
              control.onChange(!isNaN(fiatNum) && fiatNum > 0 ? fiatStore.fiatToUsdc(fiatNum).toFixed(6) : '')
              return
            }
            const value = event.target.value.replace(',', '.')
            if (!validate(value) && !!value.length) return
            control.onChange(value)
          }}
          value={isFiatMode ? fiatDisplayValue : inputValue}
          h={'24px'}
          outline={'none'}
          border={'none'}
          lineHeight={1}
          _focusVisible={{ outline: 'none', border: 'none' }}
          _active={{ outline: 'none', border: 'none' }}
          p={0}
          color={'brand.white'}
          _placeholder={{ color: 'ninja.300' }}
          fontWeight={700}
          fontSize={'sm'}
          placeholder={placeholder}
          {...inputs}
        />
        {isFiatMode && (
          <Text flexShrink={0} fontSize={'sm'} fontWeight={700} color={'ninja.300'} lineHeight={1}>
            {fiatStore.getSymbol()}
          </Text>
        )}
        {usdcHint && (
          <Text flexShrink={0} fontSize={'xs'} color={'ninja.300'} fontWeight={500} whiteSpace={'nowrap'}>
            {usdcHint}
          </Text>
        )}
        {clearable && (
          <Box
            cursor={!disabled ? 'pointer' : 'not-allowed'}
            color={'ninja.300'}
            transition={'0.2s'}
            _hover={{ color: disabled ? 'ninja.300' : 'ninja.200' }}
            onClick={() => !disabled && handleClear()}
          >
            <CloseIcon />
          </Box>
        )}
      </Flex>
    </Box>
  )
}

export function StyledInput2({
  title,
  icon,
  placeholder,
  isDisabled,
  clearable,
  infoText,
  inputs,
  setValue,
  errors,
  isDirty,
  amount,
  denom,
}: {
  title: string
  placeholder: string
  icon: React.ReactElement
  isDisabled?: boolean
  clearable?: boolean
  infoText?: string
  inputs?: InputProps
  setValue: (value: string) => void
  errors: Array<string>
  isDirty: boolean
  amount: string
  denom?: string
}) {
  const fiatStore = useFiatCurrencyStore()
  const isFiatMode = !!denom && denom === USDC_NEW_DENOM && !fiatStore.isUsd()

  const [fiatDisplayValue, setFiatDisplayValue] = useState<string>('')
  const isTypingRef = useRef(false)

  useEffect(() => {
    if (!isFiatMode) return
    if (isTypingRef.current) {
      isTypingRef.current = false
      return
    }
    const usdc = parseFloat(amount)
    setFiatDisplayValue(!isNaN(usdc) && usdc > 0 ? String(parseFloat(fiatStore.usdcToFiat(usdc).toFixed(6))) : '')
  }, [amount, isFiatMode])

  const handleClear = () => {
    setValue('')
    if (isFiatMode) setFiatDisplayValue('')
  }

  const usdcHint = isFiatMode && amount ? `≈ ${parseFloat(Number(amount).toFixed(4))} USDC` : null

  return (
    <Box
      px={3}
      py={2}
      bg={'ninja.500'}
      opacity={isDisabled ? 0.6 : 1}
      border={!!errors?.length && isDirty ? '1px solid red' : '1px solid transparent'}
      rounded={'xl'}
      flexBasis={'full'}
    >
      <Flex justifyContent={'space-between'} color={'ninja.200'} fontWeight={600} fontSize={'xs'}>
        <Text>{title}</Text>
        {infoText ? <Text>{infoText}</Text> : null}
      </Flex>
      <Flex alignItems={'center'} gap={1.5}>
        {!isFiatMode && (
          <Flex alignItems={'center'} flexShrink={0} w={'18px'} h={'18px'} justifyContent={'center'}>
            {icon}
          </Flex>
        )}
        <Input
          disabled={isDisabled}
          onChange={(event) => {
            if (isFiatMode) {
              const raw = event.target.value.replace(',', '.')
              if (raw && !/^[\d]*\.?[\d]*$/.test(raw)) return
              setFiatDisplayValue(raw)
              isTypingRef.current = true
              const fiatNum = parseFloat(raw)
              setValue(!isNaN(fiatNum) && fiatNum > 0 ? fiatStore.fiatToUsdc(fiatNum).toFixed(6) : '')
              return
            }
            setValue(event.target.value)
          }}
          value={isFiatMode ? fiatDisplayValue : amount}
          h={'24px'}
          outline={'none'}
          border={'none'}
          lineHeight={1}
          _focusVisible={{ outline: 'none', border: 'none' }}
          _active={{ outline: 'none', border: 'none' }}
          p={0}
          color={'brand.white'}
          _placeholder={{ color: 'ninja.300' }}
          fontWeight={700}
          fontSize={'sm'}
          placeholder={placeholder}
          {...inputs}
        />
        {isFiatMode && (
          <Text flexShrink={0} fontSize={'sm'} fontWeight={700} color={'ninja.300'} lineHeight={1}>
            {fiatStore.getSymbol()}
          </Text>
        )}
        {usdcHint && (
          <Text flexShrink={0} fontSize={'xs'} color={'ninja.300'} fontWeight={500} whiteSpace={'nowrap'}>
            {usdcHint}
          </Text>
        )}
        {clearable && (
          <Box
            cursor={!isDisabled ? 'pointer' : 'not-allowed'}
            color={'ninja.300'}
            transition={'0.2s'}
            _hover={{ color: isDisabled ? 'ninja.300' : 'ninja.200' }}
            onClick={() => !isDisabled && handleClear()}
          >
            <CloseIcon />
          </Box>
        )}
      </Flex>
    </Box>
  )
}
